import { useSelector } from 'react-redux'
import { cloneWithoutKeys } from 'Utils/objects'
import { escapeBlank, parsePrice, valuesToNumber } from 'Utils/Parse'
import { parsePhoneToObject } from 'Utils/PhoneHelpers'
import useRouter from 'Checkout/Hooks/useRouter'

export const useReservationsVariables = () => {
  const router = useRouter()
  const pricingData = useSelector(state => state.cart.pricing.data)
  const reservations = useSelector(state => state.cart.reservations)
  const details = useSelector(state => state.details)
  const { marketplaceOverrideUuid } = useSelector(state => state.search)
  const { couponCode, discount, homeAddress, isNonRefundable, isTripProtection,
          tripProtectionQuote } = useSelector(state => state.payment)
  const { canUseNonRefundable, canUseSiteLock, isAutoAssign, isRequest, marketplace,
          utmData } = useSelector(state => state.campground)

  const buildCustomFieldsValues = customFields => {
    const fieldValues = Object.values(customFields)

    return fieldValues.reduce((acc, { answer, customFieldId, id, value }) => {
      if (value?.length > 0)
        acc.push({ customFieldId: id, answer: value })
      else if (answer?.length > 0)
        acc.push({ customFieldId, answer })

      return acc
    }, [])
  }

  const buildHomeAddress = () => (isTripProtection ? { homeAddress: { ...homeAddress } } : {})

  const buildMarketplaceOverrideUuid = () => (
    { marketplaceOverrideUuid: marketplaceOverrideUuid || router.query.d }
  )

  const buildReferringDomain = () => (
    marketplace?.referringDomain ? { referringDomain: marketplace.referringDomain } : {}
  )

  const buildReservations = withoutNanoId => {
    const { code: discountNumber, id: discountClubId } = discount
    const guestsNoNumerics = ['petBreeds', 'useGuests', 'vehiclesTowed']

    const { email, firstName, lastName, password, phone } = details

    const reservationsToApi = reservations.map(({ dayUse, inProgress: _, ...reservation }) => {
      const { customFields, data, extras, specialRequests, vehicle, ...rest } = reservation
      const resData = withoutNanoId ? cloneWithoutKeys(rest, ['nanoId']) : rest

      const { guests, isSiteLock, productsPurchased } = extras
      const customFieldValues = buildCustomFieldsValues(customFields)

      return {
        ...resData,
        ...buildHomeAddress(),
        ...buildMarketplaceOverrideUuid(),
        ...buildReferringDomain(),
        ...buildRig(vehicle, reservation.extras.filters, data.rate),
        ...buildSite(data.site, isSiteLock, dayUse),
        ...buildTripInsuranceData(),
        ...buildUtm(),
        couponCode,
        customFieldValues,
        discountClubId,
        discountNumber,
        email,
        firstName,
        guests: valuesToNumber(guests, { except: guestsNoNumerics }),
        hasTripInsurance: isTripProtection,
        isSiteLock,
        lastName,
        nonRefundable: canUseNonRefundable && isNonRefundable,
        password,
        phone: parsePhoneToObject(phone),
        productsPurchased,
        specialRequest: specialRequests,
      }
    })

    return reservationsToApi
  }

  const buildRig = (vehicle, filters, rate) => {
    if (!rate.canAddVehicle || !vehicle) return {}

    if (vehicle.id) return { rigId: vehicle.id }

    const rig = { type: escapeBlank(vehicle.type) }
    return {
      rig: {
        ...rig,
        ...valuesToNumber({
          length: vehicle.length || filters?.maxLength,
          slideouts: vehicle.slideouts,
          width: vehicle.width || filters?.maxWidth,
          year: vehicle.year,
        }),
      },
    }
  }

  const buildSite = (site, isSiteLock, dayUse) => {
    if (isRequest || (canUseSiteLock && !isSiteLock) || (isAutoAssign && !isSiteLock) || dayUse) return null

    return { siteId: site.id }
  }

  const buildTripInsuranceData = () => {
    if (reservations.length > 1 || !isTripProtection) return {}

    return {
      tripInsurancePrice: parsePrice(tripProtectionQuote),
      totalWithTripInsurance: pricingData.total.display,
    }
  }

  const buildUtm = () => (utmData ? { utm: { ...utmData } } : {})

  return buildReservations
}
